import React from "react";
import { Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const EventTypeTag = ({ type, label }) => {
  const colorMap = {
    PREORDER_ENABLED: "geekblue",
    OUT_OF_STOCK: "volcano",
    HIDDEN: "gold",
    IN_STOCK: "green",
    LOW_IN_STOCK: "orange",
    VISIBLE: "blue",
    NEW_PRODUCT: "green",
  };
  return <Tag color={colorMap[type] || "default"}>{label}</Tag>;
};

const JsonPreview = ({ value }) => {
  if (!value) return null;
  try {
    const str = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    return (
      <Tooltip placement="left" title={<pre style={{ margin: 0, maxWidth: 400 }}>{str}</pre>}>
        <CodeBlock>{typeof value === "string" ? value : JSON.stringify(value)}</CodeBlock>
      </Tooltip>
    );
  } catch (e) {
    return <CodeBlock>{String(value)}</CodeBlock>;
  }
};

const ProductEventsTable = ({ events, loading }) => {
  const { t } = useTranslation();

  const columns = [
    {
      title: t("date"),
      dataIndex: "changedAt",
      key: "changedAt",
      sorter: (a, b) => a.changedAt - b.changedAt,
      render: (ts) => dayjs.unix(ts).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: t("product"),
      dataIndex: "productId",
      key: "productId",
      render: (id, record) => (
        <div>
          <div>{record.productName}</div>
          {!!record?.productCode && <SmallMuted>{record.productCode}</SmallMuted>}
        </div>
      ),
    },
    {
      title: t("type"),
      dataIndex: "type",
      key: "type",
      filters: [
        { text: t("PREORDER_ENABLED"), value: "PREORDER_ENABLED" },
        { text: t("OUT_OF_STOCK"), value: "OUT_OF_STOCK" },
        { text: t("HIDDEN"), value: "HIDDEN" },
      ],
      onFilter: (value, record) => record.type === value,
      render: (type) => <EventTypeTag type={type} label={t(`productEvents.${type}`)} />,
    },
    {
      title: t("old"),
      dataIndex: "oldValue",
      key: "oldValue",
      render: (v) => <JsonPreview value={v} />,
    },
    {
      title: t("new"),
      dataIndex: "newValue",
      key: "newValue",
      render: (v) => <JsonPreview value={v} />,
    },
    {
      title: t("metadata"),
      dataIndex: "metadata",
      render: (v) => {
        const pairs = Object.entries(v || {})
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
        return <JsonPreview value={pairs} />;
      },
      key: "metadata",
    },
  ];

  return (
    <Table
      size="small"
      rowKey={(r, i) => r.id || `${r.productId}-${r.type}-${r.changedAt?.toMillis?.() || i}`}
      columns={columns}
      loading={loading}
      dataSource={events}
      pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"] }}
    />
  );
};

const CodeBlock = styled.code`
  background: #f6f8fa;
  padding: 0 6px;
  border-radius: 4px;
`;

const SmallMuted = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

export default ProductEventsTable;
